// SignUp.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, CheckCircle, XCircle, Lock, Mail, User, Shield } from 'lucide-react';

const API_URL = 'https://hms.backend.hiragroupcloud.com/api/admin';

const SignUp = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'staff' // Default role is staff
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password') {
      if (value.length === 0) setPasswordStrength('');
      else if (value.length < 6) setPasswordStrength('weak');
      else if (value.length < 8) setPasswordStrength('medium');
      else setPasswordStrength('strong');
    }

    if (error) setError('');
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.username.trim()) errors.push('Username is required');
    if (!formData.email.trim()) errors.push('Email is required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.push('Email is invalid');
    if (!formData.password) errors.push('Password is required');
    else if (formData.password.length < 6) errors.push('Password must be at least 6 characters');
    if (formData.password !== formData.confirmPassword) errors.push('Passwords do not match');

    if (errors.length > 0) {
      setError(errors[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role // Send selected role
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Registration failed (${response.status})`);
      }

      // Store user data with role in localStorage
      localStorage.setItem('accessToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      // Also store role separately for easy access
      localStorage.setItem('userRole', data.user.role);

      setSuccess(`${data.user.role.charAt(0).toUpperCase() + data.user.role.slice(1)} account created successfully! Redirecting...`);

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);

    } catch (error) {
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'strong': return '#10b981';
      default: return '#9ca3af';
    }
  };

  return (
    <div>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 1
      }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src={require('../image.png')}
            alt="HMS Logo"
            style={{
              width: '180px',
              height: 'auto',
              objectFit: 'contain',
              marginBottom: '1rem'
            }}
          />

          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#004aad',
            marginBottom: '0.5rem',
          }}>
            Create Account
          </h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>
            Register as Admin or Staff
          </p>
        </div>

        {success && (
          <div style={{
            background: '#d1fae5',
            color: '#065f46',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            borderLeft: '4px solid #10b981'
          }}>
            <CheckCircle size={24} />
            <span style={{ fontWeight: '500' }}>{success}</span>
          </div>
        )}

        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            borderLeft: '4px solid #dc2626'
          }}>
            <XCircle size={24} />
            <span style={{ fontWeight: '500' }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Role Selection - NEW */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
              <Shield size={18} /> Select Role *
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem',
                border: `2px solid ${formData.role === 'admin' ? '#004aad' : '#e5e7eb'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                background: formData.role === 'admin' ? '#f0f4ff' : 'white',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={formData.role === 'admin'}
                  onChange={handleChange}
                  style={{ accentColor: '#004aad' }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: '#333' }}>Admin</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>Full access</div>
                </div>
              </label>

              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem',
                border: `2px solid ${formData.role === 'staff' ? '#004aad' : '#e5e7eb'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                background: formData.role === 'staff' ? '#f0f4ff' : 'white',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="role"
                  value="staff"
                  checked={formData.role === 'staff'}
                  onChange={handleChange}
                  style={{ accentColor: '#004aad' }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: '#333' }}>Staff</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>Limited access</div>
                </div>
              </label>
            </div>
          </div>

          {/* Username */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
              <User size={18} /> Username *
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter username"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
              <Mail size={18} /> Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
              <Lock size={18} /> Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Minimum 6 characters"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />

            {/* Password Strength Indicator */}
            {formData.password && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Strength:</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: getPasswordStrengthColor() }}>
                    {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                  </span>
                </div>
                <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%',
                    height: '100%',
                    background: getPasswordStrengthColor(),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
              <Lock size={18} /> Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Retype password"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              background: isLoading ? '#ccc' : 'linear-gradient(135deg, #004aad, #667eea)',
              color: 'white',
              border: 'none',
              padding: '1rem',
              borderRadius: '10px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              transition: 'all 0.3s ease'
            }}
          >
            <UserPlus size={22} />
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
          color: '#666',
          fontSize: '0.9rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#004aad', fontWeight: '600' }}>
            Login Here
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;